import React, { useState } from 'react';
import styled, { DefaultTheme, keyframes, withTheme } from 'styled-components';
import BillLineIcon from 'remixicon-react/BillLineIcon';
import Download2LineIcon from 'remixicon-react/Download2LineIcon';
import FileCopyLine from 'remixicon-react/FileCopyLineIcon';
import ResendInvoiceModal from '../../../containers/accounts/ResendInvoiceModal';
import { AccountState } from '../../../store/account/accountSlice';
import { useAppState } from '../../../store/appstate';
import DeliveryMethod from '../../../types/account/DeliveryMethod';
import { Links, Notification } from '../../../types/account/Notification';
import { NotificationDelivery } from '../../../types/account/NotificationDelivery';
import { NotificationElectronicInvoiceRecipient } from '../../../types/account/NotificationElectronicInvoiceRecipient';
import { NotificationEmailRecipient } from '../../../types/account/NotificationEmailRecipient';
import { NotificationPeppolRecipient } from '../../../types/account/NotificationPeppolRecipient';
import { NotificationPostalRecipient } from '../../../types/account/NotificationPostalRecipient';
import InvoiceStatus from '../../../types/invoice/InvoiceStatus';
import Button from '../../atoms/Button/Button';
import SquareIconButton from '../../atoms/Button/SquareIconButton';
import DateTime from '../../atoms/DateTime/DateTime';
import { Modal } from '../../atoms/Modal/Modal';
import Snackbar from '../../atoms/Snackbar/Snackbar';
import { openPdf } from '../../../services/helpers/openPdf';
import { useIsFlagEnabled } from '../../../services/helpers/isFlagEnabled';
import ExtendDate from '../ExtendDate';
import DueIn from '../Invoice/DueIn';
import {
    clearExtendDueDateError,
    clearResendInvoiceError,
} from '../../../store/account/accountSlice';
import { useAppDispatch } from '../../../store';
import useModalWithId from '../../../services/hooks/useModalWithId';
import StatusTypes from '../../../types/StatusTypes';
import { paymentStatus } from '../../../services/helpers/paymentStatus';
import { useNavigate } from 'react-router-dom';
import { transparentize } from 'polished';

const DOWNLOAD_PDF_AS_ATTACHMENT_FLAG = 'mx-6437-4d2a9e61-8f3b-4c07-b1e5-6a9c0d3f7e28';

interface Props {
    notification: Notification;
    links: Links;
    isCreditNote: boolean;
    openCreditFees: (notification: Notification, links: Links) => void;
    theme: DefaultTheme;
}

const NotificationInformationAndActions: React.FC<Props> = ({
    notification,
    links,
    isCreditNote,
    openCreditFees,
    theme,
}: Props) => {
    const dispatch = useAppDispatch();
    const extendInvoiceModal = useModalWithId();
    const resendInvoiceModal = useModalWithId();
    const navigate = useNavigate();
    const [openPdfFailed, setOpenPdfFailed] = useState(false);
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
    const isDownloadPdfAsAttachmentEnabled = useIsFlagEnabled(DOWNLOAD_PDF_AS_ATTACHMENT_FLAG);

    const paymentStatusInvalidDueInArray = Object.values(InvoiceStatus)
        .flatMap(e => e)
        .filter(s => s !== InvoiceStatus.NotPaid)
        .map(s => s.valueOf());

    const { error, isExtending, isExtended, extendFailed, isResending, isResent, resendFailed } =
        useAppState<AccountState>(s => s.account);

    const hasExtendable = links.extend;
    const hasResend = links.resend;
    const hasCredit = links.credit;

    const notificationId = notification.notificationId.toString();
    const showDueIn = !paymentStatusInvalidDueInArray.includes(
        paymentStatus(notification.paymentStatus, notification.canceled)
    );

    const onCloseExtendModal = () => {
        extendInvoiceModal.close();
        dispatch(clearExtendDueDateError());
    };

    const onCloseResendInvoice = () => {
        resendInvoiceModal.close();
        dispatch(clearResendInvoiceError());
    };

    const onOpenPdf = () => {
        if (!links.pdf) {
            window.open(notification.url, '_blank');
            return;
        }

        setOpenPdfFailed(false);

        if (!isDownloadPdfAsAttachmentEnabled) {
            openPdf(links.pdf).catch(() => setOpenPdfFailed(true));
            return;
        }

        setIsDownloadingPdf(true);
        openPdf(links.pdf, true)
            .catch(() => setOpenPdfFailed(true))
            .finally(() => setIsDownloadingPdf(false));
    };

    const renderPostal = (postal: NotificationPostalRecipient) => {
        return (
            <NotificationAddress>
                {postal && (
                    <>
                        <NotificationAddressLine>{postal.name}</NotificationAddressLine>
                        <NotificationAddressLine>{postal.addressLine1}</NotificationAddressLine>
                        <NotificationAddressLine>{postal.addressLine2}</NotificationAddressLine>
                        <NotificationAddressLine>{postal.coAddress}</NotificationAddressLine>
                        <NotificationAddressLine>
                            {postal.postalCode} {postal.city}
                        </NotificationAddressLine>{' '}
                    </>
                )}
            </NotificationAddress>
        );
    };

    const renderCopyableRecipient = (value: string, title: string) =>
        value && (
            <NotificationAddress>
                <RecipientWithCopy>
                    <NotificationAddressLine>{value}</NotificationAddressLine>
                    <CopyButtonContainer>
                        <CopyIconButton
                            onClick={() => navigator.clipboard.writeText(value)}
                            width="1rem"
                            title={title}
                        >
                            <FileCopyLine size={theme.icon.size.small} />
                        </CopyIconButton>
                    </CopyButtonContainer>
                </RecipientWithCopy>
            </NotificationAddress>
        );

    const renderRecipient = (delivery: NotificationDelivery) => {
        switch (delivery.method) {
            case DeliveryMethod.Post:
            case DeliveryMethod.Print:
                return renderPostal(delivery.recipient as NotificationPostalRecipient);
            case DeliveryMethod.Email:
                return renderCopyableRecipient(
                    (delivery.recipient as NotificationEmailRecipient).email,
                    'Copy email address to clipboard'
                );
            case DeliveryMethod.Peppol:
                return renderCopyableRecipient(
                    (delivery.recipient as NotificationPeppolRecipient).peppolId,
                    'Copy Peppol ID to clipboard'
                );
            case DeliveryMethod.ElectronicInvoice:
                return renderCopyableRecipient(
                    (delivery.recipient as NotificationElectronicInvoiceRecipient)
                        .electronicInvoiceAddress,
                    'Copy electronic invoice address to clipboard'
                );
            default:
                return null;
        }
    };

    const renderLatestResend = (deliveries: NotificationDelivery[]) => {
        const lastIndex = deliveries.length - 1;
        const latestResend = deliveries[lastIndex];
        const renderedRecipient = renderRecipient(latestResend);

        return (
            <>
                <Information>
                    <Label>Sent as</Label>
                    <Value>{latestResend.method}</Value>
                </Information>
                {renderedRecipient && (
                    <Information>
                        <Label>Sent to</Label>
                        <Value>{renderedRecipient}</Value>
                    </Information>
                )}
            </>
        );
    };

    return (
        <Wrapper>
            <InformationContainer>
                <Information>
                    <Label>Invoice number:</Label>
                    <Value>{notification.invoiceNumber}</Value>
                </Information>
                {!isCreditNote && (
                    <Information>
                        <Label>Payment reference:</Label>
                        <Value>{notification.paymentReference}</Value>
                    </Information>
                )}

                <Information>
                    <Label>Created:</Label>
                    <Value>
                        <DateTime>{new Date(notification.notificationDate)}</DateTime>
                    </Value>
                </Information>
                {!isCreditNote && (
                    <Information>
                        <Label>Due date:</Label>
                        <Value>
                            {showDueIn && (
                                <DueIn theme={theme} dueDate={new Date(notification.dueDate)} />
                            )}
                            <DateTime onlyDate>{new Date(notification.dueDate)}</DateTime>
                        </Value>
                    </Information>
                )}
                {notification.deliveries &&
                    notification.deliveries.length !== 0 &&
                    renderLatestResend(notification.deliveries)}
            </InformationContainer>
            <NotificationActions>
                <NotificationButtons>
                    {hasExtendable && (
                        <Button onClick={extendInvoiceModal.toggleWithId(notificationId)}>
                            Extend
                        </Button>
                    )}
                    {hasResend && (
                        <Button onClick={resendInvoiceModal.toggleWithId(notificationId)}>
                            Resend
                        </Button>
                    )}
                    {hasCredit && (
                        <Button primary onClick={() => openCreditFees(notification, links)}>
                            Credit fees
                        </Button>
                    )}
                </NotificationButtons>
                <NotificationLinks>
                    {(notification.url || links.pdf) && (
                        <SquareIconButton
                            title="Download PDF"
                            onClick={onOpenPdf}
                            disabled={isDownloadingPdf}
                        >
                            {isDownloadingPdf ? (
                                <PdfDownloadSpinner data-testid="spinner" />
                            ) : (
                                <Download2LineIcon size={15} />
                            )}
                            PDF
                        </SquareIconButton>
                    )}
                    {links.childAccount && (
                        <SquareIconButton
                            title="View transactions"
                            onClick={() => navigate(links.childAccount!.href)}
                        >
                            <BillLineIcon size={15} />
                            Payments
                        </SquareIconButton>
                    )}
                </NotificationLinks>
            </NotificationActions>

            {extendInvoiceModal.isShownWithId(notificationId) && (
                <Modal
                    small
                    isShown={extendInvoiceModal.isShownWithId(notificationId)}
                    hide={onCloseExtendModal}
                >
                    <ExtendDate
                        hide={onCloseExtendModal}
                        identifier={notification.invoiceNumber}
                        extendLink={links.extend}
                        type={StatusTypes.Invoice}
                        isExtending={isExtending}
                        isExtended={isExtended}
                        error={error}
                        extendFailed={extendFailed}
                    />
                </Modal>
            )}

            {hasResend && resendInvoiceModal.isShownWithId(notificationId) && (
                <Modal
                    medium
                    isShown={resendInvoiceModal.isShownWithId(notificationId)}
                    hide={onCloseResendInvoice}
                >
                    <ResendInvoiceModal
                        address={notification.address}
                        hide={onCloseResendInvoice}
                        identifier={notification.invoiceNumber}
                        resendLink={links.resend}
                        error={error}
                        isResending={isResending}
                        isResent={isResent}
                        resendFailed={resendFailed}
                    />
                </Modal>
            )}

            {openPdfFailed && (
                <Snackbar
                    clear={() => setOpenPdfFailed(false)}
                    open={openPdfFailed}
                    color={theme.colors.snackbar.error.background}
                    message="Could not open PDF. Try again later"
                />
            )}
        </Wrapper>
    );
};

const spin = keyframes`
    to {
        transform: rotate(360deg);
    }
`;

const PdfDownloadSpinner = styled.span`
    display: inline-block;
    box-sizing: border-box;
    width: 15px;
    height: 15px;
    border: 2px solid ${props => props.theme.colors.text.primary};
    border-bottom-color: transparent;
    border-radius: 50%;
    animation: ${spin} 0.75s linear infinite;
`;

const Wrapper = styled.div`
    display: flex;
    padding-bottom: ${props => props.theme.layout.padding.small};
`;

const InformationContainer = styled.div`
    flex: 1;
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 1rem;
`;

const Information = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: ${props => props.theme.layout.margin.small};
    padding-right: ${props => props.theme.layout.padding.small};
`;

const NotificationActions = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-self: right;
    justify-content: space-between;
    gap: 1rem;
`;

const NotificationButtons = styled.div`
    display: inline-flex;
    text-align: right;
    gap: 0.5rem;
    justify-content: flex-end;
    flex-wrap: wrap;
`;

const NotificationLinks = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-bottom: ${props => props.theme.layout.margin.small};

    button {
        min-width: 9rem;
    }
`;

const NotificationAddress = styled.div`
    display: flex;
    flex-direction: column;
`;

const NotificationAddressLine = styled.span`
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
`;

const RecipientWithCopy = styled.div`
    display: flex;
    align-items: center;
    max-width: 60rem;
`;

const CopyButtonContainer = styled.div`
    display: flex;
    align-items: center;
    padding-left: 0.5rem;
`;

const CopyIconButton = styled(Button)`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
    border: 0.1rem solid ${props => props.theme.colors.border};
`;

const Label = styled.div`
    flex: 1;
    margin-right: ${props => props.theme.layout.margin.small};
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.secondary};
`;

const Value = styled.div`
    flex: 2;
    min-width: 0;
    font-size: ${props => props.theme.text.size.medium};
`;

export default withTheme(NotificationInformationAndActions) as React.ComponentType<
    Omit<Props, 'theme'>
>;
